import React from 'react';
import { usePermission } from './PermissionContext';

const DENIED_TEXT = 'You do not have permission for this action';

/**
 * Permission-aware wrapper for a single action control (button, Link, antd Button, icon...).
 *
 *   <PermButton perm="MASTERS.VENDOR.DELETE">
 *     <Button onClick={remove}>Delete</Button>
 *   </PermButton>
 *
 * Granted          -> child rendered unchanged.
 * Denied + Hide    -> nothing rendered (default).
 * Denied + Disable -> child rendered disabled, click blocked, explanatory tooltip/aria text.
 * The behaviour per key is configured in Access Control (Operations_Permission.DeniedBehavior).
 * UX only — the API enforces the same key.
 */
const PermButton = ({ perm, behavior: behaviorOverride, deniedText = DENIED_TEXT, children }) => {
  const { can, behavior } = usePermission();

  if (can(perm)) {
    return children;
  }

  const mode = behaviorOverride || behavior(perm);
  if (mode !== 'Disable' || !React.isValidElement(children)) {
    return null;
  }

  const blockClick = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const disabledChild = React.cloneElement(children, {
    disabled: true,
    'aria-disabled': true,
    tabIndex: -1,
    onClick: blockClick,
    className: `${children.props.className || ''} disabled`.trim(),
  });

  // Disabled elements swallow pointer events, so the tooltip lives on a wrapper.
  return (
    <span className="d-inline-block" title={deniedText} aria-label={deniedText} style={{ cursor: 'not-allowed' }}>
      {disabledChild}
    </span>
  );
};

export default PermButton;
